import { z } from 'zod';
import { EP_ORDER_STATUS } from '../constants/enums.js';
import { PositiveDecimalString, PaginationQuerySchema } from './common.js';

/**
 * EP marketplace schemas.
 *
 * Field names are aligned with the runtime EpService and Prisma schema:
 *   amount_ep        — EP units the seller is offering
 *   price_cny        — fiat unit price (per 1 EP)
 *   payment_methods  — list of accepted off-chain payment rails
 */

/** Legacy PHP `php_user_transaction_ep.type`: 0 = EP1, 1 = EP2. */
export const EpMarketTypeSchema = z.enum(['ep1', 'ep2']);

export const CreateEpOrderSchema = z.object({
  /** Legacy PHP `type`: ep1 (0) or ep2 (1) — settlement asset for this listing. */
  ep_type: EpMarketTypeSchema.default('ep1'),
  amount_ep: PositiveDecimalString,
  price_cny: PositiveDecimalString,
  payment_methods: z
    .array(z.enum(['bank', 'alipay', 'wechat', 'usdt']))
    .min(1)
    .max(4),
  note: z.string().max(500).optional(),
});
export type CreateEpOrderRequest = z.infer<typeof CreateEpOrderSchema>;
// Back-compat alias for prior callers.
export const EpCreateOrderSchema = CreateEpOrderSchema;

const EpOrderIdSchema = z
  .string()
  .min(1)
  .refine((id) => z.string().uuid().safeParse(id).success || /^php:\d+$/.test(id), {
    message: 'order_id must be a UUID or php:<numeric_id>',
  });

export const ClaimEpOrderSchema = z.object({
  order_id: EpOrderIdSchema,
});
export type ClaimEpOrderRequest = z.infer<typeof ClaimEpOrderSchema>;
export const EpClaimOrderSchema = ClaimEpOrderSchema;

export const MarkEpOrderPaidSchema = z.object({
  order_id: EpOrderIdSchema,
  /** Payment proof URL or on-chain tx hash (0x…). */
  pay_proof: z
    .string()
    .max(1024)
    .refine(
      (v) => /^0x[a-fA-F0-9]{64}$/.test(v) || /^https?:\/\//.test(v),
      { message: 'pay_proof must be a tx hash or http(s) URL' },
    )
    .optional(),
});
export type MarkEpOrderPaidRequest = z.infer<typeof MarkEpOrderPaidSchema>;
export const EpMarkPaidSchema = MarkEpOrderPaidSchema;

export const ReleaseEpOrderSchema = z.object({
  order_id: EpOrderIdSchema,
});
export type ReleaseEpOrderRequest = z.infer<typeof ReleaseEpOrderSchema>;
export const EpReleaseSchema = ReleaseEpOrderSchema;

/** Seller redeems (cancels) an open EP sell listing before a buyer claims it. */
export const CancelEpOrderSchema = z.object({
  order_id: EpOrderIdSchema,
});
export type CancelEpOrderRequest = z.infer<typeof CancelEpOrderSchema>;
export const EpCancelSchema = CancelEpOrderSchema;

export const EpOrderListQuerySchema = PaginationQuerySchema.extend({
  status: z.nativeEnum(EP_ORDER_STATUS).optional(),
  seller_id: z.coerce.number().int().positive().optional(),
  buyer_id: z.coerce.number().int().positive().optional(),
  /** Filter EP1 vs EP2 (Node + PHP mirror). */
  ep_type: EpMarketTypeSchema.optional(),
  min_amount: PositiveDecimalString.optional(),
  max_amount: PositiveDecimalString.optional(),
});
export type EpOrderListQuery = z.infer<typeof EpOrderListQuerySchema>;

export const EpOrderbookQuerySchema = z.object({
  depth: z.coerce.number().int().min(1).max(50).default(10),
});
export type EpOrderbookQuery = z.infer<typeof EpOrderbookQuerySchema>;
